import React, { useState, useEffect } from "react";
import { useTranslation, Trans } from "react-i18next";
import { SBBox, SBLoading, SBTypography } from "modules/Commons";
import ProductModelAnalyserFilter from "modules/ProductModelAnalyser/components/ProductModelAnalyserFilter";
import { useModelAnalysisRequester } from "modules/ProductModelAnalyser/hooks/useModelAnalysisRequester";
import { IDemandDataVM } from "modules/ProductModelAnalyser/models/IDemandDataVM";
import { IForecastDataVM } from "modules/ProductModelAnalyser/models/IForecastDataVM";
import { IAvailableModel } from "modules/ProductModelAnalyser/models/IAvailableModel";
import { useSnackbar } from "modules/Commons/SBOverlays/SBSnackbar";
import SBForecastEvaluation from "modules/Forecast/Components/SBForecastEvaluation";
import { IHomeStatistics } from "domain/Utils/models/IHomeStatistics";
import SBEmptyState from "modules/Commons/SBLayouts/SBEmptyState";
import ConfigViewEmptyState from "views/ConfigsView/ConfigViewEmptyState";
import ProductModelSelectionTable from "./components/ProductModelSelectionTable";
import { ITimeScope } from "domain/Domain/Models/ITimeScope";
import { DateTime } from "luxon";

export default () => {
	const { t } = useTranslation();
	const snackbar = useSnackbar();
	const modelAnalysisRequester = useModelAnalysisRequester();

	const [loading, setLoading] = useState(false);
	const [chartLoading, setChartLoading] = useState(false);
	const [statistics, setStatistics] = useState<IHomeStatistics | null>(null);
	const [demandData, setDemandData] = useState<IDemandDataVM | null>(null);
	const [availableModels, setAvailableModels] = useState<IAvailableModel[]>([]);
	const [selectedModels, setSelectedModels] = useState<string[]>([]);
	const [currentForecasts, setCurrentForecasts] = useState<IForecastDataVM[]>([]);
	const [currentProductClientId, setCurrentProductClientId] = useState<string | null>(null);
	const [currentStoreClientId, setCurrentStoreClientId] = useState<string | null>(null);
	const [dateRange, setDateRange] = useState<ITimeScope | null>(null);

	useEffect(() => {
		if (demandData) {
			try {
				const mappedStatistics = modelAnalysisRequester.mapToStatistics(demandData, currentForecasts, dateRange || undefined);
				
				// TODO: Vini - CIRCUIT BREAKER: Block rendering if there is no demand data for the selected period
				// Temporarily preventing white screen crashes until root cause is found
				if (!mappedStatistics.evaluation?.demand || Object.keys(mappedStatistics.evaluation.demand).length === 0) {
					setStatistics(null);
					return;
				}

				setStatistics(mappedStatistics);
			} catch (e) {
				console.error("Crash prevented in mapToStatistics:", e);
				setStatistics(null);
			}
		}
	}, [demandData, currentForecasts, dateRange]);

	useEffect(() => {
		modelAnalysisRequester.getAvailableModels().then(models => {
			setAvailableModels(models);
		}).catch(() => {
			snackbar.error(t("errorFetchingModels"));
		});
	}, []);

	const handleApplyFilter = async (productClientId: string | null, storeClientId: string | null, newDateRange: ITimeScope | null) => {
		if (newDateRange) {
			setDateRange(newDateRange);
		}

		if (!productClientId) return;

		if (productClientId !== currentProductClientId || storeClientId !== currentStoreClientId) {
			setLoading(true);
			setStatistics(null);
			setDemandData(null);
			setSelectedModels([]);
			setCurrentForecasts([]);
			setCurrentProductClientId(productClientId);
			setCurrentStoreClientId(storeClientId);
			
			try {
				const demandRes = await modelAnalysisRequester.getDemand(productClientId, storeClientId);
				
				if (!newDateRange && demandRes.systemStart && demandRes.evaluationPeriod) {
					const end = DateTime.fromISO(demandRes.systemStart);
					const start = end.minus({ months: demandRes.evaluationPeriod });
					setDateRange({ startDate: start, endDate: end } as ITimeScope);
				}
				
				setDemandData(demandRes);
				
			} catch (error) {
				snackbar.error(t("errorFetchingAnalysis"));
			} finally {
				setLoading(false);
			}
		}
	};

	const handleSelectModel = async (modelName: string, isPrimary: boolean) => {
		if (!currentProductClientId || !demandData) return;

		let newSelected = [...selectedModels];
		
		if (isPrimary) {
			// Set as primary (index 0)
			if (newSelected[0] === modelName) {
				newSelected[0] = ""; // Deselect if already selected
			} else {
				newSelected[0] = modelName;
				if (newSelected[1] === modelName) newSelected[1] = ""; // Prevent duplicate
			}
		} else {
			// Set as benchmark (index 1)
			if (!newSelected[0]) newSelected[0] = ""; // Ensure array has enough elements
			
			if (newSelected[1] === modelName) {
				newSelected[1] = ""; // Deselect if already selected
			} else {
				newSelected[1] = modelName;
				if (newSelected[0] === modelName) newSelected[0] = ""; // Prevent duplicate
			}
		}

		// Filter out empty strings but keep the order if only index 1 is set
		const finalSelected = newSelected.filter(m => m !== "");
		
		setSelectedModels(newSelected);
		
		if (finalSelected.length === 0) {
			setCurrentForecasts([]);
			return;
		}

		// Keep a strict 2-element array matching [Primary, Secondary] slots
		setCurrentForecasts(prev => {
			const p = prev as any[];
			return [
				newSelected[0] ? (p.find(f => f && (f.modelName === newSelected[0] || f.ModelName === newSelected[0])) || null) : null,
				newSelected[1] ? (p.find(f => f && (f.modelName === newSelected[1] || f.ModelName === newSelected[1])) || null) : null
			] as any;
		});

		setChartLoading(true);
		try {
			const forecasts = await modelAnalysisRequester.getForecasts(currentProductClientId, finalSelected, currentStoreClientId);
			
			// Map forecasts back to match the slots perfectly [Primary, Secondary]
			const mappedForecasts: any[] = [null, null];
			if (newSelected[0]) {
				const f = forecasts.find((x: any) => x.modelName === newSelected[0] || x.ModelName === newSelected[0]);
				if (f) mappedForecasts[0] = f;
			}
			if (newSelected[1]) {
				const f = forecasts.find((x: any) => x.modelName === newSelected[1] || x.ModelName === newSelected[1]);
				if (f) mappedForecasts[1] = f;
			}
			
			setCurrentForecasts(mappedForecasts);
		} catch (error) {
			snackbar.error(t("errorFetchingAnalysis"));
		} finally {
			setChartLoading(false);
		}
	};

	const renderEmptyState = () => {
		if (demandData && currentProductClientId) {
			const histDemand = demandData.historicalDemand || (demandData as any).HistoricalDemand || {};
			const dates = Object.keys(histDemand).sort();
			
			if (dates.length > 0) {
				const start = DateTime.fromISO(dates[0]).toFormat("dd/MM/yyyy");
				const end = DateTime.fromISO(dates[dates.length - 1]).toFormat("dd/MM/yyyy");
				return (
					<SBBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="60vh">
						<SBTypography mt={2} py={2} variant="h5" color="darkPurple.main" fontWeight="700">
							{t("noDataInSelectedPeriod")}
						</SBTypography>
						<SBTypography textAlign="center" fontWeight="400" width={0.4}>
							<Trans i18nKey="noDataAvailablePeriodDesc" values={{ start, end }} components={{ b: <b /> }} />
							<br/><br/>
							{t("adjustDateFilterDesc")}
						</SBTypography>
					</SBBox>
				);
			} else if (demandData.availableModels && demandData.availableModels.length === 0) {
				return (
					<SBBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="60vh">
						<SBTypography mt={2} py={2} variant="h5" color="darkPurple.main" fontWeight="700">
							{t("modelNotFoundProductLevel")}
						</SBTypography>
						<SBTypography textAlign="center" fontWeight="400" width={0.4}>
							{t("productHasNoModelsMacroLevel")}
							<br/><br/>
							<Trans i18nKey="selectStoreToViewAnalyses" components={{ b: <b /> }} />
						</SBTypography>
					</SBBox>
				);
			}
		}
		return <ConfigViewEmptyState />;
	};

	return (
		<SBBox pl={11} pr={3} pt={2} pb={4} height={1} style={{ overflowY: "auto" }}>
			<SBBox mb={3}>
				<ProductModelAnalyserFilter 
					onApply={handleApplyFilter}
					initialDateRange={dateRange}
				/>
			</SBBox>

			<SBLoading loading={loading}>
				<SBEmptyState show={!!statistics} emptyState={renderEmptyState()}>
					{statistics && (
						<SBBox>
							<SBForecastEvaluation
								statistics={statistics}
								isCost={false}
								hideBenchmark={!selectedModels[1]}
								primaryLabel={selectedModels[0] ? `Forecast (${selectedModels[0]})` : "Forecast"}
								benchmarkLabel={selectedModels[1] ? `Benchmark (${selectedModels[1]})` : "Benchmark"}
								showTimeSeriesTable={true}
								loading={chartLoading}
								noBorder={true}
							>
								{availableModels.length > 0 && (
									<SBBox bgcolor="white" borderRadius="8px" p={3} mb={0}>
										<ProductModelSelectionTable 
											availableModels={availableModels}
											selectedModels={selectedModels}
											onSelectModel={handleSelectModel}
										/>
									</SBBox>
								)}
							</SBForecastEvaluation>
						</SBBox>
					)}
				</SBEmptyState>
			</SBLoading>
		</SBBox>
	);
};
